/**
 * Feuille de brouillon — le papier : les lignes de l'énoncé et de l'élève, la
 * ligne en cours, et le panneau de retour épinglé en haut (il ne pousse jamais
 * la ligne en cours). Portage du prototype validé (renderLines, renderCurrent,
 * renderFeedback).
 */

import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Calculator, Eraser, Info, StepForward } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  kernelHypothesisTexts,
  ktabClaimTexts,
  kvarClaimTexts,
  notationsOf,
  type VerdictKind,
} from '@jeraisonne/reasoner';
import { disp, isFrac, needsMath, type AtomItem, type FracItem, type LineItem } from './line-items';
import { AtomsMath, LineMath, MathListsContext } from './math-view';
import {
  claimGivens,
  givenLabels,
  programOf,
  isRed,
  type ProgramView,
  type ScratchSession,
  type SessionState,
  type SheetLine,
} from './scratch-session';
import { ProgramSimulator } from './program-simulator';
import { SignTableView, openSignTableEditor, signTableOfItems } from './sign-table';
import { GridTableView, gridOfItems, gridTableTitle, openGridTableEditor } from './grid-table';
import { useLongPress, vibrate, type LongPressGuard } from './use-long-press';

/** état visuel de la ligne en cours, selon le verdict du moteur */
const STATE_OF: Record<VerdictKind, string> = {
  empty: 'idle',
  open: 'idle',
  pending: 'idle',
  building: 'building',
  ready: 'good',
  step: 'good',
  syntax: 'bad',
  trap: 'bad',
  false: 'bad',
  unjustified: 'off',
  useless: 'off',
  dup: 'dup',
  given: 'dup',
};

// ── lignes déjà écrites ──────────────────────────────────────────────────────

/**
 * Notations de la feuille : une pastille « i » dans la MARGE, sur la toute
 * première ligne ; touchée, le sens de chaque lettre s'écrit en italique à
 * partir de cette même ligne (« t : taux d'évolution »). Rien si aucune lettre
 * n'a de sens déclaré.
 */
function Notations({ items }: { items: { name: string; meaning: string }[] }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <li className="sc-line" data-kind="notes" data-testid="scratch-notations">
      <span className="sc-row !items-start">
        <button
          type="button"
          className="sc-notes-btn"
          data-sc-keep="all"
          aria-expanded={open}
          aria-label={open ? 'Masquer les notations' : 'Afficher les notations'}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="sc-notes-pill">
            <Info className="h-4 w-4" aria-hidden />
          </span>
        </button>
        {open ? (
          <ul className="sc-notes m-0 list-none p-0" aria-label="Notations">
            {items.map((n) => (
              <li key={n.name}>
                <span className="font-display font-semibold not-italic">{disp(n.name)}</span>
                {' : '}
                {n.meaning}
              </li>
            ))}
          </ul>
        ) : (
          <span />
        )}
        <span />
      </span>
    </li>
  );
}

function GivenLine({
  items,
  src,
  copyK,
  label,
}: {
  items: LineItem[];
  src: number;
  copyK: number | null;
  /** « HR » : l'hypothèse de récurrence (moteur v26) */
  label?: string | null;
}) {
  // lot 6.21 : le tableau de l'énoncé, à remplir
  const grid = gridOfItems(items);
  return (
    <li className="sc-line" data-kind="given" data-label={label ?? undefined}>
      <span className="sc-row">
        <span className="sc-num" {...(label === 'HR' ? { title: 'Hypothèse de récurrence' } : {})}>
          {label ?? 'énoncé'}
        </span>
        <span className="sc-math" data-src={src}>
          {grid ? (
            <span className="sc-gt-wrap block" data-testid="scratch-grid-given">
              <GridTableView grid={grid.grid} compact label="Tableau de l’énoncé" />
            </span>
          ) : (
            <LineMath items={items} tag={{ active: copyK }} />
          )}
        </span>
        <span />
      </span>
    </li>
  );
}

/**
 * Étude de fonction (lot 6.20) : les choix du QCM, chacun une affirmation sur f tranchée
 * par le tableau de variations — « ? », puis « vraie » ou « fausse » (barrée).
 */
function VarClaimLines({
  texts,
  status,
}: {
  texts: string[];
  status: (boolean | null)[] | null | undefined;
}) {
  if (!texts.length) return null;
  return (
    <>
      {texts.map((text, i) => {
        const st = status?.[i] ?? null;
        return (
          <li
            key={`vc${i}`}
            className="sc-line"
            data-kind="given"
            data-claim={st === null ? 'open' : st ? 'true' : 'false'}
            data-testid="scratch-var-claim"
          >
            <span className="sc-row">
              <span
                className="sc-num sc-claim-mark"
                aria-label={
                  st === null
                    ? 'Affirmation à trancher'
                    : st
                      ? 'Affirmation vraie'
                      : 'Affirmation fausse'
                }
              >
                {st === null ? '?' : st ? 'vraie' : 'fausse'}
              </span>
              <span className="sc-math sc-claim-math sc-claim-text">{text}</span>
              <span />
            </span>
          </li>
        );
      })}
    </>
  );
}

/**
 * Feuille d'affirmations (moteur v36) : une affirmation en tête de feuille, avec sa marque —
 * « ? » tant qu'elle n'est pas tranchée, puis « vraie » ou « fausse » (barrée).
 */
function ClaimLine({
  items,
  src,
  copyK,
  status,
  first,
  at,
  form,
}: {
  items: LineItem[];
  src: number;
  copyK: number | null;
  status: boolean | null;
  first: boolean;
  at: string | null;
  /** la forme exigée (« fraction irréductible », moteur v39) */
  form?: string | null;
}) {
  const word = status === null ? '?' : status ? 'vraie' : 'fausse';
  return (
    <li
      className="sc-line"
      data-kind="given"
      data-claim={status === null ? 'open' : status ? 'true' : 'false'}
      data-testid="scratch-claim"
    >
      <span className="sc-row">
        <span
          className="sc-num sc-claim-mark"
          aria-label={
            status === null
              ? 'Affirmation à trancher'
              : status
                ? 'Affirmation vraie'
                : 'Affirmation fausse'
          }
        >
          {first ? word : '…et'}
        </span>
        <span className="sc-math sc-claim-math" data-src={src}>
          <LineMath items={items} tag={{ active: copyK }} />
          {at && <span className="sc-claim-at"> pour {at}</span>}
          {form && (
            <span className="sc-claim-at" data-testid="scratch-claim-form">
              {' '}
              ({form})
            </span>
          )}
        </span>
        <span />
      </span>
    </li>
  );
}

/** Hypothèses de l'énoncé (feuille du noyau) : « avec t > 0, v > 0 ». */
/** Trace Python (moteur v28) : le programme à tracer, puis l'appel suivi. */
/**
 * Trace Python (moteur v28) : le programme (ou l'algorithme de l'énoncé, moteur v29), l'appel
 * suivi, les tirages imposés ; compléter une ligne (moteur v29) : la ligne « ____ » à
 * écrire et les tests. Une fois la question répondue : le simulateur pas à pas.
 */
function ProgramLine({
  program,
  onSimulate,
}: {
  program: ProgramView | null;
  onSimulate: (() => void) | null;
}) {
  if (!program) return null;
  const label = program.complete
    ? 'Programme à compléter'
    : program.display
      ? 'Algorithme à tracer'
      : 'Programme à tracer';
  return (
    <li className="sc-line" data-kind="given" data-testid="scratch-program">
      <span className="sc-row !items-start">
        <span className="sc-num">{program.display ? 'algo' : 'code'}</span>
        <span className="sc-program min-w-0">
          <pre className="sc-code" aria-label={label}>
            <code>
              {program.display ??
                program.code.split('\n').map((l, i) => {
                  const blank = /^(\s*)_{3,}\s*$/.exec(l);
                  return (
                    <span key={i} className="block">
                      {blank ? (
                        <>
                          {blank[1]}
                          <span className="sc-code-blank" data-testid="scratch-program-blank">
                            ligne à compléter
                          </span>
                        </>
                      ) : (
                        l || ' '
                      )}
                    </span>
                  );
                })}
            </code>
          </pre>
          {program.call && (
            <span className="sc-code-call" data-testid="scratch-program-call">
              On trace l’appel <code>{program.call}</code>
            </span>
          )}
          {program.draws.length > 0 && (
            <span className="sc-code-call" data-testid="scratch-program-draws">
              Tirages du hasard, dans l’ordre : {program.draws.join(' ; ')}
            </span>
          )}
          {program.complete && program.checks.length > 0 && (
            <span className="sc-code-call" data-testid="scratch-program-checks">
              Complété, le programme doit donner : {program.checks.join(' ; ')}
            </span>
          )}
          {program.complete && program.output.length > 0 && (
            <span className="sc-code-call">
              Il doit afficher : <code>{program.output.join(' ')}</code>
            </span>
          )}
          {onSimulate && (
            <button
              type="button"
              className="sc-sim-open"
              data-testid="scratch-simulate"
              onClick={onSimulate}
            >
              <StepForward className="h-4 w-4" aria-hidden />
              Voir le programme s’exécuter pas à pas
            </button>
          )}
        </span>
        <span />
      </span>
    </li>
  );
}

function HypothesesLine({ texts }: { texts: string[] }) {
  if (!texts.length) return null;
  return (
    <li className="sc-line" data-kind="given" data-testid="scratch-hypotheses">
      <span className="sc-row">
        <span className="sc-num">avec</span>
        <span className="sc-math font-display">{texts.join(', ')}</span>
        <span />
      </span>
    </li>
  );
}

function StudentLine({
  line,
  n,
  src,
  copyK,
  fresh,
  onErase,
}: {
  line: SheetLine;
  n: number;
  src: number;
  copyK: number | null;
  /** HORA de la ligne qui vient d'être validée (animation radar + gain) */
  fresh: number | null;
  /** la dernière ligne s'efface (D8) : l'action apparaît quand on l'ouvre */
  onErase?: () => void;
}) {
  const [open, setOpen] = useState(false);
  // moteur v42 : une ligne « tableau de signes »
  const stable = signTableOfItems(line.items);
  // lot 6.21 : une ligne « tableau à remplir »
  const gtable = stable ? null : gridOfItems(line.items);
  // feuille libre (calcul, sans HORA) : `unchecked` est défini sur chaque ligne
  const freeLine = line.unchecked !== undefined;
  const why = line.rule
    ? `${line.rule.label} · ${disp(line.rule.why)}${line.hinted ? ' (avec un coup de pouce)' : ''}`
    : freeLine
      ? line.unchecked
        ? 'Ligne gardée sans vérification : je ne vérifie que les calculs avec des nombres.'
        : 'Calcul vérifié : il est juste.'
      : line.unverified
        ? 'Ligne gardée sans vérification : je ne sais pas encore vérifier ce passage. Elle reste sur ta feuille, sans HORA.'
        : 'Juste, mais elle n’apportait rien de nouveau. Elle reste sur ta feuille, sans HORA.';
  return (
    <li
      className={cn('sc-line', fresh !== null && 'sc-just')}
      data-kind={line.kind === 'free' ? 'free' : 'step'}
    >
      <button
        type="button"
        className="sc-row w-full text-left"
        aria-expanded={open}
        aria-label={`Ligne ${n} : ${line.text}. ${open ? 'Masquer' : 'Voir'} pourquoi.`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sc-num">
          {n}
          <span className="sc-radar" aria-hidden>
            <i />
            <i />
            <i />
          </span>
        </span>
        <span className="sc-math" data-src={src}>
          {line.code ? (
            <code className="sc-codeline">{line.text}</code>
          ) : stable ? (
            <span className="sc-st-wrap block">
              <SignTableView table={stable.table} compact />
            </span>
          ) : gtable ? (
            <span className="sc-gt-wrap block">
              <GridTableView grid={gtable.grid} compact />
            </span>
          ) : (
            <LineMath items={line.items} tag={{ active: copyK }} />
          )}
          {line.rule && <span className="sc-rule">{line.rule.label}</span>}
        </span>
        {line.kind === 'free' ? (
          <span className="sc-tag">
            {freeLine ? (line.unchecked ? 'notée' : 'juste') : line.unverified ? 'notée' : 'gardée'}
          </span>
        ) : (
          <span className="sc-tag" data-zero={line.hora ? undefined : ''}>
            +{line.hora}
          </span>
        )}
      </button>
      {open && (
        <span className="sc-why">
          {why}
          {onErase && (
            <span className="mt-1.5 flex">
              <MiniBtn ghost onClick={onErase}>
                <Eraser className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                {line.hora ? `Effacer cette ligne (−${line.hora} HORA)` : 'Effacer cette ligne'}
              </MiniBtn>
            </span>
          )}
        </span>
      )}
      {fresh !== null && fresh > 0 && (
        <span className="sc-gain" aria-hidden>
          +{fresh} HORA
        </span>
      )}
    </li>
  );
}

// ── ligne en cours ──────────────────────────────────────────────────────────

function chipContent(item: AtomItem): ReactNode {
  return needsMath(item.atoms) ? <AtomsMath atoms={item.atoms} /> : disp(item.text);
}

function LineChip({
  item,
  i,
  bad,
  session,
  guard,
}: {
  item: AtomItem;
  i: number;
  bad: boolean;
  session: ScratchSession;
  guard: LongPressGuard;
}) {
  const press = useLongPress(() => {
    vibrate(18);
    session.startInsert(i);
  }, guard);
  // moteur v42 : le tableau de signes de la ligne en cours, qu'on rouvre pour le corriger
  const stable = signTableOfItems([item]);
  if (stable)
    return (
      <button
        type="button"
        className="sc-st-chip"
        data-bad={bad ? '' : undefined}
        aria-label={`${stable.table.f ? 'Tableau de variations' : 'Tableau de signes'} : toucher pour le reprendre`}
        onClick={() => openSignTableEditor(session, stable.text)}
      >
        <SignTableView
          table={stable.table}
          compact
          {...(bad && session.getSnapshot().verdict.cell
            ? { bad: session.getSnapshot().verdict.cell }
            : {})}
        />
      </button>
    );
  // lot 6.21 : le tableau à remplir de la ligne en cours, qu'on rouvre pour le corriger
  const gtable = gridOfItems([item]);
  if (gtable)
    return (
      <button
        type="button"
        className="sc-gt-chip"
        data-bad={bad ? '' : undefined}
        aria-label="Tableau : toucher pour le reprendre"
        onClick={() => openGridTableEditor(session, gtable.text)}
      >
        <GridTableView
          grid={gtable.grid}
          compact
          {...(bad && session.getSnapshot().verdict.cell
            ? { bad: session.getSnapshot().verdict.cell }
            : {})}
        />
      </button>
    );
  return (
    <button
      type="button"
      className="sc-chip sc-chip-in"
      data-kind={item.kind}
      data-bad={bad ? '' : undefined}
      aria-label={`${disp(item.text)} : toucher pour retirer, appui long pour insérer des bouts avant`}
      {...press}
      onClick={() => {
        if (guard.current) {
          guard.current = false;
          return;
        }
        session.removeChip(i);
      }}
      onKeyDown={(e) => {
        // clavier : Maj + Entrée ouvre la case « ? »
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          session.startInsert(i);
        }
      }}
    >
      {chipContent(item)}
    </button>
  );
}

function FracEditor({
  item,
  i,
  bad,
  state,
  session,
  guard,
}: {
  item: FracItem;
  i: number;
  bad: boolean;
  state: SessionState;
  session: ScratchSession;
  guard: LongPressGuard;
}) {
  const press = useLongPress(() => session.startInsert(i), guard);
  const inside = state.cursor?.i === i;
  const slot = (part: 'num' | 'den') => {
    const active = inside && state.cursor?.part === part;
    return (
      <span
        role="button"
        tabIndex={0}
        className="sc-fslot"
        data-active={active ? '' : undefined}
        data-empty={item[part].length ? undefined : ''}
        data-sc-keep="frac"
        aria-label={`${part === 'num' ? 'Case du haut (numérateur)' : 'Case du bas (dénominateur)'}${
          active ? ', active' : ', toucher pour la remplir'
        }`}
        onClick={() => {
          if (guard.current) {
            guard.current = false;
            return;
          }
          session.setCursor(i, part);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            session.setCursor(i, part);
          }
        }}
      >
        {item[part].map((ch, j) =>
          isFrac(ch) ? null : (
            <button
              key={j}
              type="button"
              className="sc-chip"
              data-kind={ch.kind}
              data-bad={bad ? '' : undefined}
              aria-label={`Retirer ${disp(ch.text)}`}
              onClick={(e) => {
                e.stopPropagation();
                session.removeIn(i, part, j);
              }}
            >
              {chipContent(ch)}
            </button>
          ),
        )}
      </span>
    );
  };
  const empty = !item.num.length && !item.den.length;
  const editor = (
    <span
      className="sc-fedit"
      data-editing={inside ? '' : undefined}
      data-bad={bad ? '' : undefined}
      data-sc-keep="frac"
      {...(inside ? {} : press)}
    >
      {slot('num')}
      <button
        type="button"
        className="sc-fbar"
        aria-label={empty ? 'Barre de la fraction vide : la retirer' : 'Barre de fraction'}
        onClick={() => {
          if (empty) session.removeChip(i);
          else session.setCursor(i, 'num');
        }}
      />
      {slot('den')}
    </span>
  );
  if (!inside) return editor;
  // fraction vide : la touche à côté la retire (la barre seule est trop fine
  // pour un doigt) ; sinon elle fait sortir de la fraction
  return (
    <span className="inline-flex items-center gap-1">
      {editor}
      {empty ? (
        <button
          type="button"
          className="sc-chip"
          data-kind="op"
          data-sc-keep="frac"
          aria-label="Retirer la fraction vide"
          onClick={() => session.removeChip(i)}
        >
          ×
        </button>
      ) : (
        <button
          type="button"
          className="sc-chip"
          data-kind="op"
          data-sc-keep="frac"
          aria-label="Sortir de la fraction"
          onClick={() => session.exitFrac()}
        >
          →
        </button>
      )}
    </span>
  );
}

function Joker() {
  return (
    <span
      className="sc-chip"
      data-joker=""
      role="img"
      aria-label="Case « ? » : les bouts choisis se placent ici, les uns après les autres"
    >
      ?
    </span>
  );
}

function CurrentLine({
  state,
  session,
  guard,
  quiet = false,
}: {
  state: SessionState;
  session: ScratchSession;
  guard: LongPressGuard;
  /** feuille dans l'exercice : pas de mode d'emploi (appris dans la prise en main) */
  quiet?: boolean;
}) {
  const v = state.verdict;
  const kind = state.shownKind;
  const red = isRed(kind);
  // secousse + vibration à chaque nouvelle ligne rouge
  const [pop, setPop] = useState(false);
  const lastBad = useRef(state.badTick);
  useEffect(() => {
    if (state.badTick === lastBad.current) return;
    lastBad.current = state.badTick;
    vibrate([24, 40, 24]);
    setPop(false);
    const raf = requestAnimationFrame(() => setPop(true));
    const t = setTimeout(() => setPop(false), 950);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [state.badTick]);

  const { line, insertAt, cursor } = state;
  const suggestion = session.suggestion();
  const calcCost = session.calculatorCost;
  const placeholder = state.locked
    ? 'Question terminée : ta feuille reste à relire.'
    : state.copy
      ? 'Touche les bouts, ou recopie la ligne du dessus avec le premier bouton des opérations.'
      : 'Touche les bouts pour écrire une ligne.';
  return (
    <div
      className={cn('sc-cur', pop && 'sc-pop-bad')}
      data-state={STATE_OF[kind] ?? 'idle'}
      data-testid="scratch-current-line"
      data-tuto="line"
    >
      <span className="sc-dot" aria-hidden>
        <i />
      </span>
      <div className="sc-cur-chips" aria-label="Ligne en cours" role="group">
        {!line.length && !quiet && (
          <span className="text-[14px] leading-snug text-[var(--sc-muted)]">{placeholder}</span>
        )}
        {line.map((c, i) => {
          const bad = red && v.badChip !== undefined && v.badChip >= 0 && i >= v.badChip;
          return (
            <span key={i} className="contents">
              {insertAt === i && !cursor && <Joker />}
              {isFrac(c) ? (
                <FracEditor
                  item={c}
                  i={i}
                  bad={bad}
                  state={state}
                  session={session}
                  guard={guard}
                />
              ) : (
                <LineChip item={c} i={i} bad={bad} session={session} guard={guard} />
              )}
            </span>
          );
        })}
        {insertAt !== null && insertAt >= line.length && !cursor && <Joker />}
        {suggestion && (
          <button
            type="button"
            className="sc-chip"
            data-suggest=""
            data-sc-keep="all"
            data-cost={calcCost || undefined}
            aria-label={
              calcCost
                ? `Calculatrice : ajouter le résultat ${suggestion.text} (coûte ${calcCost} HORA)`
                : `Calculatrice : ajouter le résultat ${suggestion.text}`
            }
            title={
              calcCost
                ? `Calculatrice non autorisée sur cet exercice : poser le résultat coûte ${calcCost} HORA`
                : 'Calculatrice autorisée : touche pour poser le résultat'
            }
            onClick={() => {
              vibrate(8);
              session.acceptSuggestion();
            }}
          >
            <Calculator className="h-4 w-4" aria-hidden />
            {suggestion.text}
            {calcCost > 0 && <span className="sc-calc-cost">−{calcCost} HORA</span>}
          </button>
        )}
      </div>
      {line.length > 0 && !state.busy ? (
        <button
          type="button"
          className="sc-icon-btn self-start"
          aria-label="Effacer la ligne"
          onClick={() => session.clearLine()}
        >
          <Eraser className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

// ── panneau de retour ───────────────────────────────────────────────────────

function Bubble({
  tone,
  title,
  text,
  children,
}: {
  tone: 'good' | 'bad' | 'off' | 'info' | 'hint' | 'neutral';
  title?: string;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <div className="sc-bubble" data-tone={tone}>
      {title && <strong>{disp(title)}</strong>}
      {text && <p>{disp(text)}</p>}
      {children && <div className="mt-0.5 flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

function MiniBtn({
  children,
  onClick,
  ghost,
}: {
  children: ReactNode;
  onClick: () => void;
  ghost?: boolean;
}) {
  return (
    <button
      type="button"
      className="sc-mini-btn"
      data-ghost={ghost ? '' : undefined}
      data-sc-keep="frac"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function HintLine({ go, children }: { go?: boolean; children: ReactNode }) {
  return (
    <div className="sc-hint-line" data-go={go ? '' : undefined}>
      {children}
    </div>
  );
}

function Feedback({
  state,
  session,
  quiet = false,
}: {
  state: SessionState;
  session: ScratchSession;
  /** feuille dans l'exercice : pas de mode d'emploi au repos */
  quiet?: boolean;
}) {
  const v = state.verdict;
  const kind = state.shownKind;
  if (state.locked) {
    return <HintLine>Question terminée : ta feuille reste à relire.</HintLine>;
  }
  if (state.insertAt !== null && !state.cursor) {
    return (
      <Bubble
        tone="hint"
        title="Insérer des bouts"
        text={
          kind === 'ready'
            ? 'Ta ligne est juste. Touche ailleurs pour terminer l’insertion : elle sera validée.'
            : 'Les bouts choisis se placent à la place du « ? », les uns après les autres. Touche ailleurs pour terminer.'
        }
      />
    );
  }
  if (state.cursor && kind !== 'ready') {
    return (
      <HintLine go>
        {state.cursor.part === 'num'
          ? 'Case du haut : choisis les bouts du numérateur, puis touche la case du bas.'
          : 'Case du bas : choisis les bouts du dénominateur, puis touche → pour sortir de la fraction.'}
      </HintLine>
    );
  }
  if (v.hole) {
    return (
      <HintLine>Remplis les deux cases de la fraction : touche une case pour la choisir.</HintLine>
    );
  }
  const out: ReactNode[] = [];
  const f = state.flash;
  if (f && (f.type === 'hint' || !isRed(kind))) {
    out.push(<Bubble key="flash" tone={f.type} title={f.title} text={f.text} />);
    if (kind === 'empty' || (kind === 'building' && !v.complete) || kind === 'open')
      return <>{out}</>;
  }
  const clear = () => session.clearLine();
  let node: ReactNode = null;
  switch (kind) {
    case 'step':
      node = <Bubble tone="good" title="Juste, justifié, utile !" />;
      break;
    case 'ready': {
      const g = session.gainFor(v.hora ?? 0);
      node = (
        <Bubble
          tone="good"
          title={`Cette ligne est juste · ${v.rule?.label ?? ''}`}
          text="Valide-la, ou continue-la si tu veux l’allonger."
        >
          <MiniBtn onClick={() => !state.busy && session.commitLine(v)}>
            Valider la ligne · +{g} HORA
          </MiniBtn>
        </Bubble>
      );
      break;
    }
    case 'building':
      node = v.complete ? (
        <Bubble
          tone="info"
          title="Ta ligne peut encore continuer"
          text="Si elle est finie, demande-moi de la vérifier."
        >
          <MiniBtn onClick={() => session.verifyLine()}>Vérifier ma ligne</MiniBtn>
        </Bubble>
      ) : (
        <HintLine go>Bon début : continue la ligne.</HintLine>
      );
      break;
    case 'open':
      node = <HintLine>Termine ta ligne avec « = » : je la vérifierai.</HintLine>;
      break;
    case 'pending':
      node = (
        <Bubble
          tone="info"
          title="Ta ligne est finie ?"
          text="Je la vérifie quand tu me le demandes. Sinon, continue-la."
        >
          <MiniBtn onClick={() => session.verifyLine()}>Vérifier ma ligne</MiniBtn>
        </Bubble>
      );
      break;
    case 'syntax':
      node = (
        <Bubble
          tone="bad"
          title={v.title}
          text={`${v.message ?? ''} Un bout oublié au milieu ? Appui long sur le bout qui doit le suivre : tu pourras l’insérer juste avant.`}
        />
      );
      break;
    case 'trap':
    case 'false':
      node = (
        <Bubble
          tone="bad"
          title={v.title}
          text={`${v.message ?? ''}${
            // moteur v42 : un tableau de signes faux se reprend, il ne se retire pas
            signTableOfItems(state.line) || gridOfItems(state.line)
              ? ' Touche le tableau pour le reprendre.'
              : v.badChip !== undefined && v.badChip >= 0
                ? ' Touche les bouts en rouge pour les retirer.'
                : ''
          }`}
        />
      );
      break;
    case 'unjustified':
      node = (
        <Bubble tone="off" title={v.title} text={v.message}>
          <MiniBtn ghost onClick={clear}>
            Effacer la ligne
          </MiniBtn>
        </Bubble>
      );
      break;
    case 'useless':
      node = (
        <Bubble tone="off" title={v.title} text={v.message}>
          <MiniBtn onClick={() => session.keepLine()}>
            {session.free
              ? 'Garder sur ma feuille'
              : v.unknown
                ? 'Garder sans vérification'
                : 'Garder quand même'}
          </MiniBtn>
          <MiniBtn ghost onClick={clear}>
            Effacer
          </MiniBtn>
        </Bubble>
      );
      break;
    case 'dup':
    case 'given':
      node = (
        <Bubble tone="neutral" title={v.title} text={v.message}>
          <MiniBtn ghost onClick={clear}>
            Effacer la ligne
          </MiniBtn>
        </Bubble>
      );
      break;
    default:
      node = quiet ? null : (
        <HintLine>
          {state.lines.length
            ? 'Nouvelle ligne : continue ton raisonnement.'
            : 'Toucher un bout le retire. Appui long : insérer des bouts juste avant.'}
        </HintLine>
      );
  }
  if (node)
    out.push(
      <span key="v" className="contents">
        {node}
      </span>,
    );
  return <>{out}</>;
}

// ── le papier ───────────────────────────────────────────────────────────────

export function SheetPaper({
  state,
  session,
  guard,
  inline = false,
  onActivate,
}: {
  state: SessionState;
  session: ScratchSession;
  guard: LongPressGuard;
  /**
   * Exercice « sur la feuille » : les lignes s'écrivent dans la page, sous la
   * question (pas de défilement propre, pas de cadre : la page est le papier).
   */
  inline?: boolean;
  /** toucher la ligne en cours : la palette monte (exercice « sur la feuille ») */
  onActivate?: () => void;
}) {
  const paperRef = useRef<HTMLDivElement>(null);
  const curRef = useRef<HTMLDivElement>(null);
  const firstRun = useRef(true);
  // la ligne en cours reste en vue : on colle le papier en bas à chaque changement
  // (en ligne : la page défile pour la montrer juste au-dessus du dock)
  useEffect(() => {
    if (inline) {
      if (firstRun.current) {
        firstRun.current = false;
        return;
      }
      curRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const p = paperRef.current;
    if (p) p.scrollTop = p.scrollHeight;
  }, [
    inline,
    state.lines.length,
    state.line,
    state.verdict,
    state.flash,
    state.insertAt,
    state.cursor,
  ]);

  // animation de la dernière ligne validée (radar + gain), 1,8 s
  const [fresh, setFresh] = useState<{ index: number; hora: number } | null>(null);
  const lastAt = useRef(state.lastCommit?.at ?? 0);
  useEffect(() => {
    const c = state.lastCommit;
    if (!c || c.at === lastAt.current) return;
    lastAt.current = c.at;
    setFresh({ index: c.index, hora: c.hora });
    const t = setTimeout(() => setFresh(null), 1800);
    return () => clearTimeout(t);
  }, [state.lastCommit]);

  const given = state.givenLines.length;
  const labels = givenLabels(session.model);
  const claims = claimGivens(session.model);
  const program = programOf(session.model);
  // simulateur pas à pas : une fois la question répondue (ou la feuille finie)
  const [simOpen, setSimOpen] = useState(false);
  const canSimulate = !!program && (state.locked || session.done);
  const copy = state.locked || state.busy ? null : state.copy;
  const onPick = (e: MouseEvent) => {
    const el = (e.target as HTMLElement).closest?.('[data-k]');
    const src = (e.target as HTMLElement).closest?.('[data-src]');
    if (!el || !src || state.locked) return;
    e.stopPropagation();
    e.preventDefault();
    vibrate(6);
    session.pickCopy(
      Number(src.getAttribute('data-src')),
      Number(el.getAttribute('data-k')!.split(' ')[0]),
    );
  };

  return (
    // trace Python (moteur v52) : « [1, 3] » s'écrit en liste, pas en matrice
    <MathListsContext.Provider value={!!program}>
      <div
        ref={paperRef}
        className={cn(
          'relative flex flex-col pr-3',
          inline
            ? 'sc-paper-inline py-1'
            : 'sc-paper h-full min-h-0 overflow-y-auto overscroll-contain rounded-2xl py-2',
        )}
        data-testid="scratch-paper"
      >
        {!inline && (
          <div
            className="sticky top-0 z-[3] flex flex-col items-stretch gap-1.5 pb-2 pl-[54px] pr-0.5 pt-0.5"
            aria-live="polite"
            data-testid="scratch-feedback"
          >
            <Feedback state={state} session={session} />
          </div>
        )}
        {!inline && <div className="flex-1" />}
        <ol
          className="m-0 flex list-none flex-col p-0"
          aria-label="Mon raisonnement"
          onClickCapture={onPick}
        >
          <Notations items={notationsOf(session.model)} />
          <ProgramLine program={program} onSimulate={canSimulate ? () => setSimOpen(true) : null} />
          {state.givenLines.map((items, gi) =>
            claims?.[gi] ? (
              <ClaimLine
                key={`g${gi}`}
                items={items}
                src={gi}
                copyK={copy && copy.s === gi ? copy.k : null}
                status={state.claims?.[claims[gi]!.claim] ?? null}
                first={claims[gi]!.first}
                at={claims[gi]!.at}
                form={claims[gi]!.form}
              />
            ) : (
              <GivenLine
                key={`g${gi}`}
                items={items}
                label={labels[gi]}
                src={gi}
                copyK={copy && copy.s === gi ? copy.k : null}
              />
            ),
          )}
          <HypothesesLine texts={kernelHypothesisTexts(session.model)} />
          <VarClaimLines
            texts={[...kvarClaimTexts(session.model), ...ktabClaimTexts(session.model)]}
            status={state.claims}
          />
          {state.lines.map((l, i) => (
            <StudentLine
              key={`l${i}`}
              line={l}
              n={i + 1}
              src={given + i}
              copyK={copy && copy.s === given + i ? copy.k : null}
              fresh={fresh && fresh.index === i ? fresh.hora : null}
              onErase={
                i === state.lines.length - 1 && !state.locked && !state.busy
                  ? () => {
                      vibrate(8);
                      session.eraseLast();
                    }
                  : undefined
              }
            />
          ))}
        </ol>
        {inline && !state.locked && (
          <div
            className="flex flex-col items-stretch gap-1.5 py-1 pl-[54px] pr-0.5"
            aria-live="polite"
            data-testid="scratch-feedback"
          >
            <Feedback state={state} session={session} quiet />
          </div>
        )}
        {!state.locked &&
          (inline ? (
            <div
              ref={curRef}
              className="sc-cur-slot"
              data-tuto="paper-line"
              onClickCapture={() => onActivate?.()}
            >
              <CurrentLine state={state} session={session} guard={guard} quiet />
            </div>
          ) : (
            <CurrentLine state={state} session={session} guard={guard} />
          ))}
        {simOpen && program && (
          <ProgramSimulator program={program} onClose={() => setSimOpen(false)} />
        )}
      </div>
    </MathListsContext.Provider>
  );
}

/** Aperçu du papier (carte d'entrée) : les dernières lignes, en petit. */
/** Dernières lignes de la feuille (`max` = null : toutes). */
export function MiniLines({ state, max = 3 }: { state: SessionState; max?: number | null }) {
  const lines = max === null ? state.lines : state.lines.slice(-max);
  if (!lines.length) return null;
  return (
    <span className="flex flex-col gap-0.5">
      {lines.map((l, i) => (
        <span key={i} className="flex items-center gap-2 text-[15px] font-semibold">
          <span
            className={cn(
              'text-xs',
              l.kind === 'free' ? 'text-[var(--sc-muted)]' : 'text-[var(--sc-good)]',
            )}
          >
            ✓
          </span>
          <span className="font-display min-w-0 truncate">
            {l.code ? (
              <code className="sc-codeline">{l.text}</code>
            ) : signTableOfItems(l.items) ? (
              signTableOfItems(l.items)!.table.f ? (
                'Tableau de variations'
              ) : (
                'Tableau de signes'
              )
            ) : gridOfItems(l.items) ? (
              // le nom du tableau (« Tableau de restes »), sinon lu sur ses lignes
              l.text && !l.text.startsWith('⊟') ? (
                l.text
              ) : (
                gridTableTitle(gridOfItems(l.items)!.grid)
              )
            ) : (
              <LineMath items={l.items} />
            )}
          </span>
        </span>
      ))}
    </span>
  );
}
